import { NgIf } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FolderDto, GalleryDto } from '@picflow/types';
import { Subscription } from 'rxjs';
import { sleep } from 'src/app/lib/sleep';
import { FolderService } from 'src/app/services/folder.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { ContextMenuComponent } from 'src/app/shared/components/context-menu/context-menu.component';
import { CreateGalleryContextComponent } from '../../../account/components/create-gallery-context/create-gallery-context.component';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { InputPreDirective } from '../../../shared-extra/directives/input-pre.directive';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { FinderContentComponent } from '../finder-content/finder-content.component';

@Component({
  selector: 'app-finder',
  templateUrl: './finder.component.html',
  styleUrls: ['./finder.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    ButtonDirective,
    SvgComponent,
    FormsModule,
    ReactiveFormsModule,
    InputFieldComponent,
    InputPreDirective,
    FinderContentComponent,
    ContextMenuComponent,
    CreateGalleryContextComponent,
  ],
})
export class FinderComponent implements OnInit, OnDestroy {
  @Output() onSelectFolder = new EventEmitter();
  @Output() onSelectGallery = new EventEmitter();
  @Input() blockGallerySelection = false;
  @Input() allowFolderAndGalleryAsTarget = false;
  @Input() hideSubGalleries = false;
  @Input() currentFolder: FolderDto = undefined;
  @Input() currentGallery: GalleryDto = undefined;
  @Input() disableList: (FolderDto | GalleryDto)[] = [];
  @Input() filterOut: (FolderDto | GalleryDto)[] = [];
  @Input() reOpenFn: (...args) => void = undefined;
  @ViewChild('searchField', { static: false, read: ElementRef }) searchField: ElementRef<HTMLInputElement>;

  allFolders: FolderDto[];
  allGalleries: GalleryDto[];
  noResults = false;

  searchMode = false;
  searchTerm = '';
  searchForm = new FormGroup({
    search: new FormControl(''),
  });
  formSub: Subscription;

  constructor(private folderService: FolderService, private galleryService: GalleryService) {}

  async ngOnInit() {
    const [folders, galleries] = await Promise.all([
      this.folderService.getFolders(),
      this.galleryService.getAllGalleriesIncludingSubgalleries({ ignorePublicAccessLevel: true }),
    ]);
    this.allFolders = folders;
    this.allGalleries = galleries;

    if (this.currentGallery) {
      this.currentFolder = this.currentFolder || this.allFolders.find(f => f.id === this.currentGallery.folder);
      this.onSelectFolder.emit(this.currentFolder);
      this.onSelectGallery.emit(this.currentGallery);
    }

    this.formSub = this.searchForm.valueChanges.subscribe(() => {
      this.searchTerm = this.searchForm.value.search;
    });
  }

  async enableSearchMode() {
    this.searchMode = true;
    await sleep(0);
    this.searchField.nativeElement.focus();
  }

  disableSearchMode() {
    this.searchMode = false;
    this.searchForm.reset();
    this.searchTerm = '';
  }

  upFolder() {
    if (!this.allFolders) return;
    if (!this.currentFolder) return;
    if (!this.currentFolder.parent_folder) this.selectFolder(undefined);
    else this.selectFolder(this.allFolders.find(f => f.id === this.currentFolder.parent_folder));
  }

  selectFolder(folder: FolderDto) {
    this.disableSearchMode();
    this.currentFolder = folder;
    this.onSelectFolder.emit(this.currentFolder);
    if (this.allowFolderAndGalleryAsTarget) {
      this.selectGallery(undefined);
    } else {
      this.onSelectGallery.emit(undefined);
    }
    return false;
  }

  selectGallery(gallery: GalleryDto) {
    if (gallery === this.currentGallery) {
      this.currentGallery = undefined;
    } else {
      this.currentGallery = gallery;
    }
    this.onSelectGallery.emit(this.currentGallery);
    return false;
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
  }
}
